import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  Search,
  Filter,
  Trash2,
  Power,
  PowerOff,
  Loader2,
  PackageX,
  CheckCircle2,
  AlertCircle,
  ArrowDownLeft,
} from "lucide-react";
import {
  fetchAgentProducts,
  deleteAgentProducts,
  toggleAgentActive,
  clearProducts,
  resetPagination,
} from "../../RTK/agents/AgentProducts";
import Loader from "../../components/loader/Loader";

function AgentProducts() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    products,
    agent,
    count,
    next,
    loading,
    deleteLoading,
    toggleLoading,
    hasMore,
    currentPage,
    totalPages,
  } = useSelector((state) => state.agentProducts);

  const [search, setSearch] = useState("");
  const [productType, setProductType] = useState("");
  const [status, setStatus] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // ✅ Ref for Infinite Scroll
  const loadMoreRef = useRef(null);

  // ✅ Initial load
  useEffect(() => {
    if (agentId) {
      dispatch(
        fetchAgentProducts({
          agentId,
          page: 1,
          search: "",
          productType: "",
          status: "",
          append: false,
        }),
      );
    }

    return () => {
      dispatch(clearProducts());
    };
  }, [agentId, dispatch]);

  // ✅ Infinite Scroll Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && next) {
          console.log("🔄 Loading more products...", {
            currentPage,
            totalPages,
            hasMore,
          });

          dispatch(
            fetchAgentProducts({
              agentId,
              page: currentPage + 1,
              search,
              productType,
              status,
              append: true,
            }),
          );
        }
      },
      {
        threshold: 0.1,
        rootMargin: "100px",
      },
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [
    dispatch,
    agentId,
    hasMore,
    loading,
    next,
    currentPage,
    totalPages,
    search,
    productType,
    status,
  ]);

  const handleApplyFilters = () => {
    dispatch(resetPagination());
    dispatch(
      fetchAgentProducts({
        agentId,
        page: 1,
        search,
        productType,
        status,
        append: false,
      }),
    );
  };

  const handleToggleAgent = async () => {
    if (agent) {
      await dispatch(
        toggleAgentActive({
          agentId,
          isActive: !agent.is_active,
        }),
      );
      dispatch(resetPagination());
      dispatch(
        fetchAgentProducts({
          agentId,
          page: 1,
          search,
          productType,
          status,
          append: false,
        }),
      );
    }
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map((p) => p.id));
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId],
    );
  };

  const handleDeleteProducts = async () => {
    if (selectedProducts.length === 0) return;

    await dispatch(
      deleteAgentProducts({
        agentId,
        productIds: selectedProducts,
      }),
    );

    setSelectedProducts([]);
    setShowDeleteConfirm(false);
    dispatch(resetPagination());

    dispatch(
      fetchAgentProducts({
        agentId,
        page: 1,
        search,
        productType,
        status,
        append: false,
      }),
    );
  };

  const StatusBadge = ({ status }) => {
    const colors = {
      available: "bg-green-500/20 text-green-400 border-green-500/30",
      unavailable: "bg-red-500/20 text-red-400 border-red-500/30",
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium border ${
          colors[status] || "bg-gray-500/20 text-gray-400 border-gray-500/30"
        }`}
      >
        {status === "available" ? "متاح" : "غير متاح"}
      </span>
    );
  };

  const TypeBadge = ({ type }) => {
    const typeLabels = {
      quantity: "كمية",
      package: "باقة",
      stock: "مخزون",
    };

    const typeColors = {
      quantity: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      package: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      stock: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium border ${
          typeColors[type] || "bg-gray-500/20 text-gray-400 border-gray-500/30"
        }`}
      >
        {typeLabels[type] || type}
      </span>
    );
  };

  // ✅ Loading for initial load only
  if (loading && products.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white p-4 md:p-6" dir="rtl">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4 w-full md:w-auto">
          <button
            onClick={() => navigate("/agent")}
            className="p-2 bg-gray-800 rounded-xl hover:bg-gray-700 transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1 md:flex-initial">
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3 flex-wrap">
              <span className="truncate">{agent?.name}</span>
              {agent?.is_active ? (
                <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-green-400 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 md:w-6 md:h-6 text-yellow-400 flex-shrink-0" />
              )}
            </h1>
            <p className="text-sm md:text-base text-gray-400 mt-1">
              إدارة منتجات الوكيل
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto flex-wrap">
          <button
            onClick={handleToggleAgent}
            disabled={toggleLoading}
            className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg transition-all text-sm md:text-base flex-1 md:flex-initial justify-center ${
              agent?.is_active
                ? "bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
                : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
            } disabled:opacity-50`}
          >
            {toggleLoading ? (
              <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
            ) : agent?.is_active ? (
              <PowerOff className="w-4 h-4 md:w-5 md:h-5" />
            ) : (
              <Power className="w-4 h-4 md:w-5 md:h-5" />
            )}
            <span className="hidden sm:inline">
              {agent?.is_active ? "تعطيل الوكيل" : "تفعيل الوكيل"}
            </span>
            <span className="sm:hidden">
              {agent?.is_active ? "تعطيل" : "تفعيل"}
            </span>
          </button>

          {selectedProducts.length > 0 && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-3 md:px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-all text-sm md:text-base flex-1 md:flex-initial justify-center"
            >
              <Trash2 className="w-4 h-4 md:w-5 md:h-5" />
              <span className="hidden sm:inline">حذف المحدد</span>
              <span>({selectedProducts.length})</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-800 p-4 md:p-6 mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Filter className="w-5 h-5 md:w-6 md:h-6 text-blue-400" />
          <h2 className="text-lg md:text-xl font-bold text-white">
            تصفية المنتجات
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="md:col-span-2 lg:col-span-2">
            <label className="block text-sm text-gray-400 mb-2">
              البحث بالاسم
            </label>
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="ابحث عن منتج..."
                className="w-full bg-gray-800 text-white pr-10 px-4 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm md:text-base"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">
              نوع المنتج
            </label>
            <select
              value={productType}
              onChange={(e) => setProductType(e.target.value)}
              className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm md:text-base"
            >
              <option value="">الكل</option>
              <option value="quantity">كمية</option>
              <option value="package">باقة</option>
              <option value="stock">مخزون</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">الحالة</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm md:text-base"
            >
              <option value="">الكل</option>
              <option value="available">متاح</option>
              <option value="unavailable">غير متاح</option>
            </select>
          </div>
        </div>

        <button
          onClick={handleApplyFilters}
          disabled={loading}
          className="mt-4 w-full md:w-auto flex items-center justify-center gap-2 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Filter className="w-5 h-5" />
              تطبيق الفلتر
            </>
          )}
        </button>
      </div>

      {/* Products Count */}
      <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-sm md:text-base text-gray-400">
          إجمالي المنتجات: <span className="text-white font-bold">{count}</span>
        </p>
        {products.length > 0 && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedProducts.length === products.length}
              onChange={handleSelectAll}
              className="w-5 h-5 rounded border-gray-700 bg-gray-800 checked:bg-blue-500 cursor-pointer"
            />
            <span className="text-sm md:text-base text-gray-400">
              تحديد الكل
            </span>
          </label>
        )}
      </div>

      {/* Products List */}
      {products.length === 0 && !loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <PackageX className="w-16 h-16 md:w-20 md:h-20 text-gray-600 mb-4" />
          <p className="text-lg md:text-xl text-gray-400">لا توجد منتجات</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 mb-8">
            {products.map((product) => (
              <div
                key={product.id}
                className={`bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border p-4 md:p-6 transition-all ${
                  selectedProducts.includes(product.id)
                    ? "border-blue-500 shadow-lg shadow-blue-500/20"
                    : "border-gray-800 hover:border-gray-700"
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <input
                    type="checkbox"
                    checked={selectedProducts.includes(product.id)}
                    onChange={() => handleSelectProduct(product.id)}
                    className="w-5 h-5 rounded border-gray-700 bg-gray-800 checked:bg-blue-500 cursor-pointer flex-shrink-0"
                  />
                  <div className="flex gap-2 flex-wrap justify-end">
                    <StatusBadge status={product.product_status} />
                    <TypeBadge type={product.product_type} />
                  </div>
                </div>

                <h3 className="text-lg md:text-xl font-bold text-white mb-2 truncate">
                  {product.product_name}
                </h3>

                <div className="space-y-2 text-xs md:text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">معرف المنتج:</span>
                    <span className="text-white font-medium">
                      #{product.product_id}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-400">السعر الحالي:</span>
                    <span className="text-green-400 font-bold">
                      ${product.current_price}
                    </span>
                  </div>

                  {product.agent_price && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">سعر الوكيل:</span>
                      <span className="text-blue-400 font-bold">
                        ${parseFloat(product.agent_price).toFixed(4)}
                      </span>
                    </div>
                  )}

                  {product.no_active_agent && (
                    <div className="flex items-center gap-2 text-yellow-400 bg-yellow-500/10 p-2 rounded-lg">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span className="text-xs">لا يوجد وكيل نشط</span>
                    </div>
                  )}

                  <div className="pt-2 border-t border-gray-700 text-xs text-gray-500">
                    تاريخ الإنشاء:{" "}
                    {new Date(product.product_created_at).toLocaleDateString(
                      "ar-EG",
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ✅ Infinite Scroll Loader - نفس التصميم من UserDetailsPage */}
          {hasMore && (
            <div
              ref={loadMoreRef}
              className="py-8 flex flex-col items-center justify-center gap-2"
            >
              {loading ? (
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
                  <span className="text-gray-400 text-sm">
                    جاري تحميل المزيد من المنتجات...
                  </span>
                </div>
              ) : (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-gray-400 text-sm mb-2 animate-pulse">
                    <ArrowDownLeft className="w-4 h-4" />
                    <span>قم بالتمرير لتحميل المزيد</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    صفحة {currentPage} من {totalPages}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ✅ End Message */}
          {!loading && !hasMore && products.length > 0 && (
            <p className="text-center text-gray-400 py-4 border-t border-gray-700 flex items-center justify-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              تم تحميل جميع المنتجات ({count} منتج)
            </p>
          )}

          {/* ✅ Page Info */}
          {products.length > 0 && (
            <div className="text-center text-sm text-gray-500 py-2 flex items-center justify-center gap-2">
              <PackageX className="w-4 h-4" />
              <span>
                عرض {products.length} من {count} منتج
                {totalPages > 1 && ` (صفحة ${currentPage} من ${totalPages})`}
              </span>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700 p-6 max-w-md w-full">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-red-500/20 rounded-xl flex-shrink-0">
                <Trash2 className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-bold text-white">
                  تأكيد الحذف
                </h3>
                <p className="text-sm text-gray-400">
                  هل أنت متأكد من حذف {selectedProducts.length} منتج؟
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleDeleteProducts}
                disabled={deleteLoading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all disabled:opacity-50 text-sm md:text-base"
              >
                {deleteLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    جاري الحذف...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-5 h-5" />
                    حذف
                  </>
                )}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-all disabled:opacity-50 text-sm md:text-base"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentProducts;
